import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  VStack,
  HStack,
  Icon,
  Button,
  useColorModeValue,
  Badge,
  List,
  ListItem,
  ListIcon,
  Flex,
} from '@chakra-ui/react';
import {
  FaServer,
  FaShieldAlt,
  FaChartLine,
  FaCog,
  FaDatabase,
  FaMobile,
  FaDesktop,
  FaCheck,
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

const SolutionsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const bgColor = useColorModeValue('gray.50', 'gray.800');
  const cardBg = useColorModeValue('white', 'gray.700');

  const handleCheckDemos = () => {
    navigate('/demo');
  };

  const handleViewPricing = () => {
    navigate('/contact');
  };

  const handleContactUs = () => {
    navigate('/contact');
  };

  const solutions = [
    {
      title: t('solutionsPage.cards.network.title'),
      description: t('solutionsPage.cards.network.description'),
      icon: FaServer,
      features: [
        t('solutionsPage.cards.network.features.f1'),
        t('solutionsPage.cards.network.features.f2'),
        t('solutionsPage.cards.network.features.f3'),
        t('solutionsPage.cards.network.features.f4'),
        t('solutionsPage.cards.network.features.f5'),
        t('solutionsPage.cards.network.features.f6'),
      ],
      badge: t('solutionsPage.cards.network.badge'),
      badgeColor: 'green',
    },
    {
      title: t('solutionsPage.cards.standalone.title'),
      description: t('solutionsPage.cards.standalone.description'),
      icon: FaShieldAlt,
      features: [
        t('solutionsPage.cards.standalone.features.f1'),
        t('solutionsPage.cards.standalone.features.f2'),
        t('solutionsPage.cards.standalone.features.f3'),
        t('solutionsPage.cards.standalone.features.f4'),
        t('solutionsPage.cards.standalone.features.f5'),
        t('solutionsPage.cards.standalone.features.f6'),
      ],
      badge: t('solutionsPage.cards.standalone.badge'),
      badgeColor: 'blue',
    },
    {
      title: t('solutionsPage.cards.tournaments.title'),
      description: t('solutionsPage.cards.tournaments.description'),
      icon: FaChartLine,
      features: [
        t('solutionsPage.cards.tournaments.features.f1'),
        t('solutionsPage.cards.tournaments.features.f2'),
        t('solutionsPage.cards.tournaments.features.f3'),
        t('solutionsPage.cards.tournaments.features.f4'),
        t('solutionsPage.cards.tournaments.features.f5'),
        t('solutionsPage.cards.tournaments.features.f6'),
      ],
      badge: t('solutionsPage.cards.tournaments.badge'),
      badgeColor: 'purple',
    },
  ];

  const services = [
    {
      title: t('servicesSection.customDevelopment.title'),
      description: t('servicesSection.customDevelopment.description'),
      icon: FaCog,
    },
    {
      title: t('servicesSection.integrationServices.title'),
      description: t('servicesSection.integrationServices.description'),
      icon: FaDatabase,
    },
    {
      title: t('servicesSection.mobileSolutions.title'),
      description: t('servicesSection.mobileSolutions.description'),
      icon: FaMobile,
    },
    {
      title: t('servicesSection.desktopApplications.title'),
      description: t('servicesSection.desktopApplications.description'),
      icon: FaDesktop,
    },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box bg={bgColor} py={16}>
        <Container maxW='7xl'>
          <VStack spacing={6} textAlign='center'>
            <Heading size='2xl' color='primary.500'>
              {t('solutionsPage.hero.title')}
            </Heading>
            <Text fontSize='xl' color='gray.500' maxW='3xl'>
              {t('solutionsPage.hero.subtitle')}
            </Text>
            <HStack spacing={4}>
              <Button
                size='lg'
                colorScheme='primary'
                onClick={handleCheckDemos}
              >
                {t('solutionsPage.hero.buttons.checkDemos')}
              </Button>
              <Button size='lg' variant='outline' onClick={handleViewPricing}>
                {t('solutionsPage.hero.buttons.viewPricing')}
              </Button>
            </HStack>
          </VStack>
        </Container>
      </Box>

      {/* Solutions Section */}
      <Box bg={bgColor} py={16}>
        <Container maxW='7xl'>
          <VStack spacing={12}>
            <VStack spacing={4} textAlign='center'>
              <Heading color='primary.500' size='xl'>
                {t('solutionsPage.solutions.title')}
              </Heading>
              <Text fontSize='lg' color='gray.500' maxW='2xl'>
                {t('solutionsPage.solutions.subtitle')}
              </Text>
            </VStack>

            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
              {solutions.map((solution, index) => (
                <Box
                  key={index}
                  bg={cardBg}
                  p={8}
                  borderRadius='lg'
                  boxShadow='md'
                  border='1px solid'
                  borderColor={useColorModeValue('gray.200', 'gray.600')}
                  position='relative'
                  display='flex'
                  flexDirection='column'
                  h='full'
                >
                  {solution.badge && (
                    <Badge
                      position='absolute'
                      top={4}
                      right={4}
                      colorScheme={solution.badgeColor}
                      variant='solid'
                    >
                      {solution.badge}
                    </Badge>
                  )}

                  <Flex
                    direction='column'
                    gap={4}
                    align='flex-start'
                    flex={1}
                    w='full'
                  >
                    <Icon as={solution.icon} boxSize={8} color='primary.500' />
                    <Heading
                      color={useColorModeValue('gray.900', 'white')}
                      size='md'
                    >
                      {solution.title}
                    </Heading>
                    <Text color={useColorModeValue('gray.900', 'gray.400')}>
                      {solution.description}
                    </Text>

                    <List spacing={2} w='full' flex={1}>
                      {solution.features.map((feature, idx) => (
                        <ListItem
                          color={useColorModeValue('gray.600', 'white')}
                          key={idx}
                        >
                          <ListIcon as={FaCheck} color='green.500' />
                          {feature}
                        </ListItem>
                      ))}
                    </List>

                    <Button
                      alignSelf='stretch'
                      colorScheme='primary'
                      mt='auto'
                      onClick={handleContactUs}
                    >
                      {t('solutionsPage.cta.scheduleDemo')}
                    </Button>
                  </Flex>
                </Box>
              ))}
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* Services Section */}
      <Box bg={bgColor} py={16}>
        <Container maxW='7xl'>
          <VStack spacing={12}>
            <VStack spacing={4} textAlign='center'>
              <Heading size='xl' color={useColorModeValue('gray.900', 'white')}>
                {t('servicesSection.title')}
              </Heading>
              <Text fontSize='lg' color='gray.500' maxW='2xl'>
                {t('servicesSection.subtitle')}
              </Text>
            </VStack>

            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={8}>
              {services.map((service, index) => (
                <Box
                  key={index}
                  bg={cardBg}
                  p={6}
                  borderRadius='lg'
                  textAlign='center'
                  boxShadow='md'
                >
                  <VStack spacing={4}>
                    <Icon as={service.icon} boxSize={10} color='primary.500' />
                    <Heading
                      size='md'
                      color={useColorModeValue('gray.900', 'white')}
                    >
                      {service.title}
                    </Heading>
                    <Text
                      color={useColorModeValue('gray.600', 'gray.400')}
                      fontSize='sm'
                    >
                      {service.description}
                    </Text>
                  </VStack>
                </Box>
              ))}
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box py={{ base: 12, md: 16 }}>
        <Container maxW='4xl'>
          <Box
            bg='primary.500'
            color='white'
            p={{ base: 6, md: 8 }}
            borderRadius='xl'
            textAlign='center'
            w='full'
            maxW='4xl'
          >
            <VStack spacing={{ base: 4, md: 6 }}>
              <Heading size={{ base: 'md', md: 'lg' }}>
                {t('solutionsPage.cta.title')}
              </Heading>
              <Text fontSize={{ base: 'md', md: 'lg' }} maxW='2xl'>
                {t('solutionsPage.cta.subtitle')}
              </Text>
              <VStack spacing={4} w={{ base: 'full', md: 'auto' }}>
                <HStack
                  spacing={4}
                  flexWrap={{ base: 'wrap', md: 'nowrap' }}
                  justify='center'
                >
                  <Button
                    size={{ base: 'md', md: 'lg' }}
                    variant='solid'
                    bg='white'
                    color='primary.500'
                    onClick={handleContactUs}
                    _hover={{ bg: 'gray.100' }}
                    w={{ base: 'full', sm: 'auto' }}
                    minW={{ base: 'auto', sm: '180px' }}
                  >
                    {t('solutionsPage.cta.scheduleDemo')}
                  </Button>
                  <Button
                    size={{ base: 'md', md: 'lg' }}
                    variant='outline'
                    borderColor='white'
                    color='white'
                    onClick={handleContactUs}
                    _hover={{ bg: 'whiteAlpha.200' }}
                    w={{ base: 'full', sm: 'auto' }}
                    minW={{ base: 'auto', sm: '180px' }}
                  >
                    {t('solutionsPage.cta.contactSales')}
                  </Button>
                </HStack>
              </VStack>
            </VStack>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default SolutionsPage;
